import React, { useState, useEffect, useRef, useMemo } from 'react';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// --- SVG ICONS ---
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const PlusIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const TrashIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3"></path>
  </svg>
);

const SaveIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"></path>
    <polyline points="17 21 17 13 7 13 7 21"></polyline>
    <polyline points="7 3 7 8 15 8"></polyline>
  </svg>
);

const API_URL = '/api';

// --- SEARCHABLE DROPDOWN COMPONENT ---
// --- SEARCHABLE DROPDOWN COMPONENT ---
const SearchableDropdown = ({ 
  options = [],
  value, 
  onChange, 
  placeholder = "Select", 
  error = false, 
  width = "w-full",
  getOptionLabel = (option) => option.name || option.label || option.toString(),
  getOptionValue = (option) => option.value || option.id || option.toString(),
  getOptionSecondaryLabel = (option) => option.code || option.id || "",
  disabled = false, 
  className = "",
  isLoading = false,
  menuWidth = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isKeyboardNav, setIsKeyboardNav] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, bottom: 'auto' });
  const dropdownListRef = useRef(null);
  const searchInputRef = useRef(null);

  // --- HELPER: Find the scrolling parent ---
  const findScrollParent = (element) => {
    if (!element || element === document.body) return document.body;
    const style = window.getComputedStyle(element);
    const isScrollable = 
      style.overflow === 'auto' || style.overflow === 'scroll' || 
      style.overflowY === 'auto' || style.overflowY === 'scroll' ||
      style.overflowX === 'auto' || style.overflowX === 'scroll';
    
    if (isScrollable && (element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth)) {
      return element;
    }
    return findScrollParent(element.parentElement);
  };

  // --- Handle Position (Includes flip logic) ---
  useEffect(() => {
    if (!isOpen) return;
    const handleUpdatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const finalWidth = menuWidth ? parseInt(menuWidth) : rect.width;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const minRequired = 250;
        
        if (spaceBelow < minRequired && spaceAbove > spaceBelow) {
          setPosition({ top: 'auto', bottom: window.innerHeight - rect.top, left: rect.left, width: finalWidth });
        } else {
          setPosition({ top: rect.bottom, bottom: 'auto', left: rect.left, width: finalWidth });
        }
      }
    };
    handleUpdatePosition();
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    const scrollParent = findScrollParent(triggerRef.current);
    if (scrollParent && scrollParent !== document.body) {
      scrollParent.addEventListener('scroll', handleUpdatePosition);
    }
    return () => {
      window.removeEventListener('resize', handleUpdatePosition);
      window.removeEventListener('scroll', handleUpdatePosition);
      if (scrollParent && scrollParent !== document.body) {
        scrollParent.removeEventListener('scroll', handleUpdatePosition);
      }
    };
  }, [isOpen, menuWidth]);

  // --- FIX: Only scroll when using KEYBOARD navigation ---
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0 && isKeyboardNav) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'start', behavior: 'auto' });
      }
    }
  }, [highlightedIndex, isOpen, isKeyboardNav]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setSearchTerm('');
      setHighlightedIndex(-1);
      setIsKeyboardNav(false);
      setTimeout(() => { searchInputRef.current.focus(); }, 0);
    }
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(option => {
      const label = (getOptionLabel(option) || '').toString().toLowerCase();
      const secondaryLabel = (getOptionSecondaryLabel(option) || '').toString().toLowerCase();
      return label.includes(term) || secondaryLabel.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionSecondaryLabel]);

  const handleKeyDown = (e) => {
    if (!isOpen) { if (e.key === 'ArrowDown' || e.key === 'Enter') { setIsOpen(true); return; } return; }
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); setIsKeyboardNav(true); setHighlightedIndex(prev => { if (prev < filteredOptions.length - 1) return prev + 1; else if (filteredOptions.length > 0) return 0; return prev; }); break;
      case 'ArrowUp': e.preventDefault(); setIsKeyboardNav(true); setHighlightedIndex(prev => { if (prev > 0) return prev - 1; else if (filteredOptions.length > 0) return filteredOptions.length - 1; return prev; }); break;
      case 'Enter': e.preventDefault(); if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) handleSelectOption(filteredOptions[highlightedIndex]); break;
      case 'Escape': setIsOpen(false); break; default: break;
    }
  };

  const handleSelectOption = (option) => { onChange(getOptionValue(option)); setIsOpen(false); setSearchTerm(''); setHighlightedIndex(-1); };

  useEffect(() => {
    const handleClickOutside = (event) => { 
      // Fixed logic to prevent closing when clicking inside the fixed dropdown itself
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !event.target.closest('.fixed.z-\\[9999\\]')) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => { document.removeEventListener('mousedown', handleClickOutside); };
  }, []);

  const selectedOption = options.find(option => getOptionValue(option) === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`${width} border rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        style={{ borderColor: error ? 'rgb(248, 113, 113)' : 'rgb(230, 217, 204)' }}
        onClick={() => !disabled && !isLoading && setIsOpen(!isOpen)}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="truncate">
          {isLoading && <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full mr-2 inline-block"></div>}
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg" 
          style={{ 
            top: position.top !== 'auto' ? `${position.top}px` : 'auto', 
            bottom: position.bottom !== 'auto' ? `${position.bottom}px` : 'auto', 
            left: `${position.left}px`, 
            width: `${position.width}px` 
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input ref={searchInputRef} type="text" placeholder="Search..." className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyDown={handleKeyDown} />
          </div>
          <div className="max-h-60 overflow-y-scroll" ref={dropdownListRef}>
            {filteredOptions.length > 0 ? filteredOptions.map((option, index) => (
                <div key={getOptionValue(option)} className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${highlightedIndex === index ? 'bg-blue-100 text-blue-900' : ''} ${value === getOptionValue(option) ? 'bg-[#E6F0FF]' : ''}`} onClick={() => handleSelectOption(option)} onMouseEnter={() => { setIsKeyboardNav(false); setHighlightedIndex(index); }}>
                  <div className="font-medium">{getOptionLabel(option)}</div>
                  {getOptionSecondaryLabel(option) && <div className="text-xs text-gray-500">{getOptionSecondaryLabel(option)}</div>}
                </div>
              )) : <div className="px-3 py-2 text-gray-500">No options found</div>}
          </div>
        </div>
      )}
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
};

// --- MAIN MODAL COMPONENT ---
const MaterialReturnFormModal = ({ open, onClose, initialData, onSubmit, onFormSuccess }) => {
  
  // --- STATE ---
  const initialHeader = {
    MaterialReturnID: null, MaterialReturnNo: '', ReturnDate: new Date().toISOString().split('T')[0],
    VendorCode: '', ProjectHeaderId: '', ProjectNo: '',
    FromFeederId: '', ToFeederId: '', TransferType: 'Transfer from Contractor',
    Remarks: '', Status: 'Open', ApprovalStatus: '',
    Circle: '', District: '', Division: '', SubDivision: '', SubStation: ''
  };

  const initialLine = {
    MaterialReturnLineID: null, ProductCode: '', Description: '', UnitOfMeasure: 'NOS',
    AvailableQty: 0, Quantity: 0, OriginalQuantity: 0, ItemCategoryCode: '', LineNumber: 1000,TaskNo: '', ProjectLineNo: '',Remark: '',ActivityId: null,
    SubActivityId: null
  };

  const [header, setHeader] = useState(initialHeader);
  const [lines, setLines] = useState([initialLine]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [surveyBOQs, setSurveyBOQs] = useState([]);
const [surveyBOQLoading, setSurveyBOQLoading] = useState(false);
  // Updated Toast State
  const [toast, setToast] = useState({
    open: false,
    type: 'error',
    title: '',
    message: ''
  });

  // Ref for toast timer to fix closing issue
  const toastTimerRef = useRef(null);

  const [projects, setProjects] = useState([]);
  const [projectFeeders, setProjectFeeders] = useState([]);
  const [projectItems, setProjectItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [nextReturnNumber, setNextReturnNumber] = useState('');
   const [projectActivities, setProjectActivities] = useState([]);

  // --- RESIZABLE STATE & REFS ---
  const [columnWidths, setColumnWidths] = useState({
    lineNo: 80,
    productCode: 200,
    description: 280,
    uom: 100,
    taskNo: 120,
    availQty: 120,
    qty: 120,
    action: 60,
    remark:280,
    activity: 200,
    subActivity: 200
  });

  const resizingRef = useRef(null);

  const isHeaderLocked = initialData && initialData.header && (initialData.header.BcSuccess === true || initialData.header.BcSuccess === 1);

  // --- EFFECTS ---
  useEffect(() => {
    if (open) {
      setIsMaximized(false);
      resetForm();
      fetchProjects();
      fetchVendors();

      const isEditMode = initialData && initialData.header && initialData.header.MaterialReturnID;
      if (!isEditMode) fetchNextReturnNumber();

      if (initialData) {
        const sourceData = initialData.header ? initialData.header : initialData;
        setHeader(prev => ({
          ...initialHeader,
          ...sourceData,
          ReturnDate: sourceData.ReturnDate ? sourceData.ReturnDate.split('T')[0] : new Date().toISOString().split('T')[0]
        }));

       if (sourceData.ProjectNo) {
  const loadDependentData = async () => {
    const feeders = await fetchProjectFeeders(sourceData.ProjectNo);

    fetchProjectItems(sourceData.ProjectNo);
    fetchActivities(sourceData.ProjectNo);

    if (sourceData.FromFeederId && feeders) {
      const feeder = feeders.find(f => f.FeederId == sourceData.FromFeederId);

      if (feeder) {
        await fetchSurveyBOQ(sourceData.ProjectNo, feeder.Code);
      }
    }
  };

  loadDependentData();
}
        
        const editLines = initialData.lines || sourceData.Lines || [];
        const loadedLines = editLines.length > 0 ? editLines.map(l => ({ 
            ...initialLine, 
            ...l,
            OriginalQuantity: l.Quantity || 0 // Set OriginalQuantity for validation
        })) : [initialLine];
        
        setLines(loadedLines);

        // Fetch latest Available Qty in Edit Mode
                // Fetch latest Available Qty in Edit Mode
        // if (isEditMode && sourceData.VendorCode && sourceData.ProjectHeaderId && sourceData.FromFeederId) {
        //     loadedLines.forEach((line, idx) => {
        //         if (line.ProductCode && line.ActivityId && line.SubActivityId) {
        //             fetchAvailableQty(idx, line.ProductCode, line.TaskNo, {
        //                 vendorCode: sourceData.VendorCode,
        //                 projectHeaderId: sourceData.ProjectHeaderId,
        //                 feederId: sourceData.FromFeederId,
        //                 activityId: line.ActivityId,
        //                 subActivityId: line.SubActivityId
        //             });
        //         }
        //     });
        // }
      }
    }
  }, [open, initialData]);
const fetchSurveyBOQ = async (projectNo, feederCode) => {
  if (!projectNo || !feederCode) return;

  try {
    setSurveyBOQLoading(true);
    const token = localStorage.getItem('authToken');

    const res = await fetch(
      `${API_URL}/surveyboq-for-dpr?projectNo=${projectNo}&feederCode=${encodeURIComponent(feederCode)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const data = await res.json();
    const boqList = data.data || [];

    setSurveyBOQs(boqList);

    // ✅ IMPORTANT: re-set selected BOQ if editing
    if (header.SurveyBOQID) {
      const exists = boqList.find(b => b.SurveyBOQID == header.SurveyBOQID);
      if (!exists) {
        setHeader(prev => ({ ...prev, SurveyBOQID: null }));
      }
    }

  } catch (err) {
    console.error("Error fetching BOQ:", err);
    setSurveyBOQs([]);
  } finally {
    setSurveyBOQLoading(false);
  }
};
  // --- APIs ---
  const fetchNextReturnNumber = async () => { 
    try { const token = localStorage.getItem('authToken'); const res = await fetch(`${API_URL}/material-return/next-number`, { headers: { 'Authorization': `Bearer ${token}` } }); const data = await res.json(); setNextReturnNumber(data.nextMaterialReturnNo || 'MRT-0001'); } catch (e) {} };
  const fetchProjects = async () => { try { const token = localStorage.getItem('authToken'); const res = await fetch(`${API_URL}/project-filter`, { headers: { 'Authorization': `Bearer ${token}` } }); setProjects(await res.json()); } catch (e) {} };
  const fetchVendors = async () => { try { const token = localStorage.getItem('authToken'); const res = await fetch(`${API_URL}/vendors`, { headers: { 'Authorization': `Bearer ${token}` } }); setVendors(await res.json()); } catch (e) {} };

   const fetchActivities = async (projectNo) => {
    if (!projectNo) {
      setProjectActivities([]);
      return;
    }
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/activity-master/project/${projectNo}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch activities');
      const data = await res.json();
      setProjectActivities(data || []);
    } catch (e) {
      console.error("Error fetching activities:", e);
      setProjectActivities([]);
    }
  };
  
  const fetchProjectFeeders = async (projectNo) => {
    if (!projectNo) return;
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/project/${projectNo}/feeders-filter`, { headers: { 'Authorization': `Bearer ${token}` } });
const data = await res.json();
setProjectFeeders(data);
return data;    } catch (e) { setProjectFeeders([]); }
  };

  const fetchProjectItems = async (projectNo) => {
    if (!projectNo) return;
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/project/${projectNo}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setProjectItems(data.Items || []);
    } catch (e) { setProjectItems([]); }
  };

  // Fetch Available Qty - Updated to accept optional context
    const fetchAvailableQty = async (index, productCode, taskNo, context = {}) => {
    const vendorCode = context.vendorCode || header.VendorCode;
    const projectHeaderId = context.projectHeaderId || header.ProjectHeaderId;
    const feederId = context.feederId || header.FromFeederId;
    const activityId = context.activityId || lines[index]?.ActivityId;
    const subActivityId = context.subActivityId || lines[index]?.SubActivityId;

    if (!vendorCode || !projectHeaderId || !feederId || !productCode) {
      return;
    }

    // NEW: Require Activity and SubActivity to fetch
    if (!activityId || !subActivityId) {
      // Just clear the available qty instead of fetching
      setLines(prev => {
        const newLines = prev.map((line, i) => {
          if (i === index) {
            return { ...line, AvailableQty: 0 };
          }
          return line;
        });
        return newLines;
      });
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams({
        vendorCode: vendorCode,
        projectHeaderId: projectHeaderId,
        feederId: feederId, 
        productCode: productCode,
        taskNo: taskNo || '',
        activityId: activityId || '',
        subActivityId: subActivityId || ''
      });
      
      const res = await fetch(`${API_URL}/material-return/available-qty?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();

      if (data.AvailableQty !== undefined) {
        setLines(prev => {
          const newLines = prev.map((line, i) => {
            if (i === index) {
              return { ...line, AvailableQty: data.AvailableQty };
            }
            return line;
          });
          return newLines;
        });
      }
    } catch (e) {
      console.error("Failed to fetch available qty", e);
    }
  };
  // --- HANDLERS ---
  const handleHeaderChange = (field, value) => {
    setHeader(prev => ({ ...prev, [field]: value }));
  
    if (field === 'ProjectHeaderId') {
      const projectObj = projects.find(p => p.ProjectHeaderId === value);
      
      setHeader(prev => ({
        ...prev,
        ProjectHeaderId: value,
        ProjectNo: projectObj?.No || '',
        ToFeederId: '',
        FromFeederId: '',
        Circle: projectObj?.Circle || '',
        District: projectObj?.District || '',
        SurveyBOQID: null
      }));
     
      setLines([initialLine]);
      
      if (projectObj?.No) {
        fetchProjectFeeders(projectObj.No);
        fetchProjectItems(projectObj.No);
        fetchActivities(projectObj.No);
      } else {
        setProjectFeeders([]);
        setProjectItems([]);
        setProjectActivities([]);
      }
    }
    
        if (field === 'VendorCode') {
      lines.forEach((line, idx) => {
        if (line.ProductCode && line.ActivityId && line.SubActivityId) {
          fetchAvailableQty(idx, line.ProductCode, line.TaskNo, {
            vendorCode: value
          });
        } else if (line.ProductCode) {
          // Clear available qty if activity/subactivity not selected
          setLines(prev => prev.map((l, i) => i === idx ? { ...l, AvailableQty: 0 } : l));
        }
      });
    }
  };

  const handleFeederChange = (field, value) => {
    setHeader(prev => ({ ...prev, [field]: value }));

       if (field === 'FromFeederId') {
      const selectedFeeder = projectFeeders.find(f => f.FeederId == value);
      if (selectedFeeder) {
        fetchSurveyBOQ(header.ProjectNo, selectedFeeder.Code); // 🔥 ADD THIS
        setHeader(prev => ({
          ...prev,
          Circle: selectedFeeder.Circle || '',
          District: selectedFeeder.District || '',
          Division: selectedFeeder.Division || '',
          SubDivision: selectedFeeder.SubDivision || '',
          SubStation: selectedFeeder.SubStation || '',
          SurveyBOQID: null
        }));
      }
     
      
      lines.forEach((line, idx) => {
        if (line.ProductCode && line.ActivityId && line.SubActivityId) {
          fetchAvailableQty(idx, line.ProductCode, line.TaskNo, {
            feederId: value
          });
        } else if (line.ProductCode) {
          setLines(prev => prev.map((l, i) => i === idx ? { ...l, AvailableQty: 0 } : l));
        }
      });
    }
  };

   const handleLineChange = async (index, field, value) => {
    const newLines = [...lines];
    
    if (field === 'ActivityId') {
      newLines[index].SubActivityId = null;
      newLines[index].AvailableQty = 0;
      newLines[index].Quantity = 0;
    }
    
    if (field === 'ProductCode') {
        const valueStr = String(value); 
        const selectedItem = projectItems.find(item => String(item.ProjectLineId) === valueStr);
        
        if (selectedItem) {
            newLines[index] = {
                ...newLines[index],
                ProductCode: selectedItem.ItemNo,
                Description: selectedItem.Description || selectedItem.ProductName,
                UnitOfMeasure: selectedItem.UOM || 'NOS',
                TaskNo: selectedItem.JobTaskNo || '',
                ProjectLineNo: String(selectedItem.ProjectLineId), 
                AvailableQty: 0,
                Quantity: 0,
                OriginalQuantity: 0
            };
            
            // Only fetch if Activity and SubActivity are already selected
            if (newLines[index].ActivityId && newLines[index].SubActivityId) {
              fetchAvailableQty(index, selectedItem.ItemNo, selectedItem.JobTaskNo, {
                activityId: newLines[index].ActivityId,
                subActivityId: newLines[index].SubActivityId
              });
            }
        } else {
            console.warn("Item not found for ID:", valueStr);
        }
    } else if (field === 'Quantity') {
      let val = parseFloat(value) || 0;
      
      const originalQty = parseFloat(newLines[index].OriginalQuantity) || 0;
      const availableQty = parseFloat(newLines[index].AvailableQty) || 0;
      const maxAllowed = originalQty + availableQty;

      if (val > maxAllowed) {
        showToast(`Cannot return more than ${maxAllowed} (Original: ${originalQty} + Available: ${availableQty})`, 'error', 'Validation');
        val = maxAllowed;
      }
      
      if (val < 0) val = 0;
      
      newLines[index][field] = val;
    } else if (field === 'ActivityId' || field === 'SubActivityId') {
      newLines[index][field] = value;
      newLines[index].AvailableQty = 0;
      newLines[index].Quantity = 0;
      
      // Fetch available qty only when BOTH are selected
      if (newLines[index].ActivityId && newLines[index].SubActivityId && newLines[index].ProductCode) {
        fetchAvailableQty(index, newLines[index].ProductCode, newLines[index].TaskNo, {
          activityId: newLines[index].ActivityId,
          subActivityId: newLines[index].SubActivityId
        });
      }
    } else {
      newLines[index][field] = value;
    }
    
    setLines(newLines);
  };

  const addLine = () => setLines([...lines, { ...initialLine, LineNumber: (lines.length + 1) * 100 }]);
  const removeLine = (index) => { if (lines.length > 1) setLines(lines.filter((_, i) => i !== index)); };
  const resetForm = () => { setHeader(initialHeader); setLines([initialLine]); setNextReturnNumber(''); setProjectFeeders([]); setProjectItems([]); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!header.ProjectNo) return showToast('Project is required.', 'error', 'Required');
    if (!header.VendorCode) return showToast('Vendor is required.', 'error', 'Required');
    if (!header.ToFeederId) return showToast('To Feeder is required.', 'error', 'Required');
    if (!header.FromFeederId) return showToast('From Feeder is required.', 'error', 'Required');
    if (!header.SurveyBOQID) return showToast('Survey BOQ is required.', 'error', 'Required');

    const validLines = lines.filter(l => l.ProductCode && l.Quantity > 0);
    if (validLines.length === 0) return showToast('Add at least one item with quantity.', 'error', 'Required');

    setIsSubmitting(true);
    setLoading(true);
    
    try {
      const token = localStorage.getItem('authToken');
      const payload = {
header: {
  ...header,
  SurveyBOQID: header.SurveyBOQID ? parseInt(header.SurveyBOQID) : null,
  ProjectHeaderId: header.ProjectHeaderId ? parseInt(header.ProjectHeaderId) : null,
  FromFeederId: header.FromFeederId ? parseInt(header.FromFeederId) : null,
  ToFeederId: header.ToFeederId ? parseInt(header.ToFeederId) : null
},        lines: validLines.map(l => ({ ...l, Quantity: parseFloat(l.Quantity) || 0 }))
      };

      const editId = initialData?.header?.MaterialReturnID;
      const url = editId ? `${API_URL}/material-return/${editId}` : `${API_URL}/material-return`;
      const method = editId ? 'PUT' : 'POST';

      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(payload) });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'Failed');
      if (onFormSuccess) onFormSuccess(result, editId ? 'Updated' : 'Created');
      onClose();
    } catch (error) {
      showToast(error.message, 'error', 'Error');
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  // Updated showToast helper with timer cleanup
  const showToast = (message, type = 'error', title = 'Error', duration = 4000) => {
    // Clear existing timer if any
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }

    setToast({ open: true, message, type, title });

    toastTimerRef.current = setTimeout(() => {
      setToast(prev => ({ ...prev, open: false }));
      toastTimerRef.current = null;
    }, duration);
  };

  // --- RESIZABLE HANDLERS ---
  const startResize = (columnKey, startX) => {
    resizingRef.current = {
      columnKey,
      startX,
      startWidth: columnWidths[columnKey]
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopResize);
  };

  const handleMouseMove = (e) => {
    if (!resizingRef.current) return;

    const { columnKey, startX, startWidth } = resizingRef.current;
    const delta = e.clientX - startX;

    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: Math.max(60, startWidth + delta)
    }));
  };

  const stopResize = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', stopResize);
    resizingRef.current = null;
  };

  // --- RESIZABLE TH COMPONENT ---
  const ResizableTh = ({ width, onResize, children, align = 'left' }) => (
    <th
      style={{ width }}
      className={`relative p-2 border ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'} select-none`}
    >
      {children}
      <div
        className="absolute top-0 right-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400"
        onMouseDown={(e) => onResize(e.clientX)}
      />
    </th>
  );

  if (!open) return null;

  return (
    <>
      <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        <style>{`
          .hide-scrollbar::-webkit-scrollbar{display:none}
          
          .resizable-table {
            table-layout: fixed;
            min-width: max-content;
          }

          @keyframes toastIn {
            from { opacity: 0; transform: translate(-50%, -16px) scale(0.98); }
            to { opacity: 1; transform: translate(-50%, 0) scale(1); }
          }

          @keyframes toastProgress {
            from { width: 100%; }
            to { width: 0%; }
          }

          .animate-toastIn {
            animation: toastIn 0.25s ease-out;
          }

          .animate-toastProgress {
            animation: toastProgress 4s linear forwards;
          }
        `}</style>
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'} ${isMaximized ? 'h-full max-w-full' : 'h-full max-w-6xl'}`} tabIndex={0}>
          
          {/* --- MODAL HEADER --- */}
          <div className="flex-shrink-0 p-8 pb-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-[#384959]">{initialData?.header?.MaterialReturnID ? 'Edit Material Return' : 'Create Material Return'}</h2>
              <div className="flex items-center gap-2">
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={() => setIsMaximized(!isMaximized)}>
                  {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
                </button>
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                  <CloseIcon size={24} />
                </button>
              </div>
            </div>
          </div>

          {/* --- FORM BODY --- */}
          <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
            <form onSubmit={handleSubmit} className="space-y-6 pb-6">
              
              {/* --- HEADER SECTION --- */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">Return Information</h3>
                
                {/* Grid Layout: 2 Columns */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Row 1 */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Return No</label>
                    <input type="text" value={initialData?.header?.MaterialReturnID ? header.MaterialReturnNo : nextReturnNumber} disabled className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase-input text-gray-600" />
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Date</label>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker value={header.ReturnDate ? dayjs(header.ReturnDate) : null} onChange={(v) => handleHeaderChange('ReturnDate', v?.format('YYYY-MM-DD'))} disabled={isHeaderLocked} format="DD/MM/YYYY" slotProps={{ textField: { size: 'small', className: 'w-full', required: true } }} />
                    </LocalizationProvider>
                  </div>

                  {/* Row 2 */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Vendor / Contractor <span className='text-red-600'>*</span></label>
                    <SearchableDropdown options={vendors} value={header.VendorCode} onChange={(v) => handleHeaderChange('VendorCode', v)} disabled={isHeaderLocked} placeholder="Select Contractor" getOptionLabel={(v) => v.vendor_name} getOptionValue={(v) => v.vendor_code} getOptionSecondaryLabel={(v) => v.vendor_code} />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Project <span className='text-red-600'>*</span></label>
                    <SearchableDropdown
                        options={projects}
                        value={header.ProjectHeaderId}
                        onChange={(val) => handleHeaderChange('ProjectHeaderId', val)}
                        disabled={isHeaderLocked}
                        placeholder="Select Project"
                        getOptionLabel={(p) => `${p.No} - ${p.Description}`}
                        getOptionValue={(p) => p.ProjectHeaderId}
                        getOptionSecondaryLabel={(p) => p.No}
                    />
                </div>

                  {/* Row 3 */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">From Feeder (Source) <span className='text-red-600'>*</span></label>
                    <SearchableDropdown options={projectFeeders} value={header.FromFeederId} onChange={(v) => handleFeederChange('FromFeederId', v)} disabled={isHeaderLocked || !header.ProjectNo} placeholder="Select Source" getOptionLabel={(f) => `${f.Code} - ${f.Description}`} getOptionValue={(f) => f.FeederId} getOptionSecondaryLabel={(f) => f.Code} />
                  </div>
<div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">
    Survey BOQ <span className='text-red-600'>*</span>
  </label>

  <SearchableDropdown
    options={surveyBOQs}
value={header.SurveyBOQID ? Number(header.SurveyBOQID) : null}    onChange={(val) =>
  setHeader(prev => ({
    ...prev,
    SurveyBOQID: val
  }))
}
    placeholder={
      !header.FromFeederId
        ? "Select Feeder first..."
        : surveyBOQLoading
        ? "Loading BOQ..."
        : "Select Survey BOQ..."
    }
    disabled={!header.FromFeederId || surveyBOQLoading}
    isLoading={surveyBOQLoading}
    getOptionLabel={(b) => b.SurveyBOQNo}
    getOptionValue={(b) => b.SurveyBOQID}
    getOptionSecondaryLabel={(b) => b.SurveyBOQNo}
  />
</div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">To Feeder (Destination) <span className='text-red-600'>*</span></label>
                    <SearchableDropdown options={projectFeeders.filter(f => f.FeederId != header.FromFeederId)} value={header.ToFeederId} onChange={(v) => handleFeederChange('ToFeederId', v)} disabled={isHeaderLocked || !header.ProjectNo} placeholder="Select Destination" getOptionLabel={(f) => `${f.Code} - ${f.Description}`} getOptionValue={(f) => f.FeederId} getOptionSecondaryLabel={(f) => f.Code} />
                  </div>

                  {/* Row 4 - Auto-filled Details */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Circle</label>
                    <input type="text" value={header.Circle} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">District</label>
                    <input type="text" value={header.District} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50" />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Division</label>
                    <input type="text" value={header.Division} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50" />
                  </div>
                   <div className="flex flex-col gap-2">
                     <label className="block text-sm font-medium text-[#64748b]">Sub Division</label>
                     <input type="text" value={header.SubDivision} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50" />
                  </div>
                   <div className="flex flex-col gap-2">
                     <label className="block text-sm font-medium text-[#64748b]">Sub Station</label>
                     <input type="text" value={header.SubStation} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50" />
                  </div>

                  {/* Status - Full Width */}
                   <div className="flex flex-col gap-2 md:col-span-2">
                      <label className="block text-sm font-medium text-[#64748b]">Status</label>
                      <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-[#F1F5F9]">
                        {!initialData ? <span className="text-xs font-medium text-gray-600 italic">Open</span> 
                        : header.ApprovalStatus === 'PENDING' ? <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">Pending Approval (Level {header.CurrentLevel || 1})</span>
                        : header.ApprovalStatus === 'REJECTED' ? <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200">Rejected</span>
                        : header.ApprovalStatus === 'APPROVED' ? (header.SendToBC === 1 || header.SendToBC === true ? <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-200">Sent to BC Successfully</span> : <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-200">Approved (Ready to Send BC)</span>)
                        : <span className="text-xs text-gray-500 italic">{(header.SendToBC === 1 || header.SendToBC === true) ? 'Sent to BC Successfully' : 'Open'}</span>}
                      </div>
                    </div>

                  {/* Remarks - Full Width */}
                  <div className="flex flex-col gap-2 md:col-span-2">
                    <label className="block text-sm font-medium text-[#64748b]">Remarks</label>
                    <textarea value={header.Remarks} onChange={e => handleHeaderChange('Remarks', e.target.value)} disabled={isHeaderLocked} rows="2" className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"></textarea>
                  </div>
                </div>
              </div>

              {/* --- LINES SECTION --- */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#384959]">Return Lines</h3>
                  <button type="button" className="px-4 py-2 bg-[#6A89A7] text-white rounded-lg hover:bg-[#88BDF2] flex items-center transition-colors" onClick={addLine}>
                    <PlusIcon className="mr-1" /> Add Item
                  </button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="resizable-table w-full border border-[#E2E8F0]">
                    <thead>
                      <tr className="bg-[#BDDDFC] text-[#384959]">
                        <ResizableTh width={columnWidths.lineNo} onResize={(x) => startResize('lineNo', x)} align="center">
                          Line No
                        </ResizableTh>
                        <ResizableTh width={columnWidths.productCode} onResize={(x) => startResize('productCode', x)}>
                          Product Code
                        </ResizableTh>
                        <ResizableTh width={columnWidths.description} onResize={(x) => startResize('description', x)}>
                          Description
                        </ResizableTh>
                        <ResizableTh width={columnWidths.uom} onResize={(x) => startResize('uom', x)}>
                          UOM
                        </ResizableTh>
                        <ResizableTh width={columnWidths.taskNo} onResize={(x) => startResize('taskNo', x)}>
                          TaskNo
                        </ResizableTh>
                         <ResizableTh width={columnWidths.activity} onResize={(x) => startResize('activity', x)}>
                          Activity
                        </ResizableTh>
                        <ResizableTh width={columnWidths.subActivity} onResize={(x) => startResize('subActivity', x)}>
                          Sub Activity
                        </ResizableTh>
                        <ResizableTh width={columnWidths.availQty} onResize={(x) => startResize('availQty', x)} align="right">
                          Avail Qty
                        </ResizableTh>
                        <ResizableTh width={columnWidths.qty} onResize={(x) => startResize('qty', x)} align="right">
                          Qty
                        </ResizableTh>
                        <ResizableTh width={columnWidths.remark} onResize={(x) => startResize('remark', x)}>
                        Remark
                      </ResizableTh>
                        <ResizableTh width={columnWidths.action} onResize={(x) => startResize('action', x)} align="center">
                          Action
                        </ResizableTh>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, idx) => (
                        <tr key={idx} className="border-t border-[#E2E8F0] hover:bg-white transition-colors">
                          <td className="p-2 border" style={{ width: columnWidths.lineNo }}>
                            <input type="number" value={line.LineNumber} onChange={e => handleLineChange(idx, 'LineNumber', e.target.value)} className="w-full border border-[#E2E8F0] rounded px-2 py-1 text-center bg-gray-50" readOnly />
                          </td>
                          <td className="p-2 border" style={{ width: columnWidths.productCode }}>
                            <SearchableDropdown options={projectItems} value={String(line.ProjectLineNo || '')}  onChange={(v) => handleLineChange(idx, 'ProductCode', v)} placeholder="Select Item" getOptionLabel={(i) => `${i.ItemNo}-${i.Description} (Task: ${i.JobTaskNo || 'N/A'})`} getOptionValue={(i) => String(i.ProjectLineId)}  menuWidth={600}/>
                          </td>
                          <td className="p-2 border" style={{ width: columnWidths.description }}>
                            <input type="text" value={line.Description} readOnly className="w-full border border-[#E2E8F0] rounded px-2 py-1 bg-gray-50" />
                          </td>
                          <td className="p-2 border" style={{ width: columnWidths.uom }}>
                            <input type="text" value={line.UnitOfMeasure} readOnly className="w-full border border-[#E2E8F0] rounded px-2 py-1 bg-gray-50 uppercase-input" />
                          </td>
                          <td className="p-2 border" style={{ width: columnWidths.taskNo }}>
                            <input 
                                type="text" 
                                value={line.TaskNo || ''} 
                                className="w-full border border-[#E2E8F0] rounded px-2 py-1 bg-gray-50 read-only:bg-gray-100" 
                                readOnly 
                            />
                          </td>
                          <td className="p-2 border" style={{ width: columnWidths.activity }}>
                            <SearchableDropdown
                              options={projectActivities.filter(a => a.IsParentActivity === 1 || a.IsParentActivity === true)} 
                              value={line.ActivityId}
                              onChange={(val) => handleLineChange(idx, 'ActivityId', val)}
                              placeholder="Select Activity"
                              getOptionLabel={(a) => `${a.Code} - ${a.Description}`}
                              getOptionValue={(a) => a.ActivityId}
                              getOptionSecondaryLabel={(a) => a.Code}
                            />
                          </td>
                          <td className="p-2 border" style={{ width: columnWidths.subActivity }}>
                            {(() => {
                              // Logic to filter sub-activities based on selected activity
                              const selectedActivityObj = projectActivities.find(a => a.ActivityId == line.ActivityId);
                              const subActivityOptions = selectedActivityObj 
                                ? projectActivities.filter(a => a.ParentActivityCode === selectedActivityObj.Code)
                                : [];
                              
                              return (
                                <SearchableDropdown
                                  options={subActivityOptions} 
                                  value={line.SubActivityId}
                                  onChange={(val) => handleLineChange(idx, 'SubActivityId', val)}
                                  placeholder="Select Sub-Activity"
                                  getOptionLabel={(a) => `${a.Code} - ${a.Description}`}
                                  getOptionValue={(a) => a.ActivityId}
                                  getOptionSecondaryLabel={(a) => a.Code}
                                  disabled={!line.ActivityId} // Disabled until Activity is selected
                                />
                              );
                            })()}
                          </td>
                          <td className="p-2 border" style={{ width: columnWidths.availQty }}>
                            {(!line.ActivityId || !line.SubActivityId) ? (
                              <input 
                                type="text" 
                                value="Select Activity & Sub-Activity" 
                                readOnly 
                                className="w-full border border-[#E2E8F0] rounded px-2 py-1 bg-gray-100 text-gray-400 italic text-xs"
                              />
                            ) : (
                              <input 
                                type="number" 
                                value={line.AvailableQty} 
                                readOnly 
                                inputMode="decimal"  
                                step="0.000001"  
                                className="w-full border border-[#E2E8F0] rounded px-2 py-1 text-right bg-blue-50 font-medium text-blue-800" 
                              />
                            )}
                          </td>
                          <td className="p-2 border" style={{ width: columnWidths.qty }}>
                            <input type="number" value={line.Quantity} onChange={e => handleLineChange(idx, 'Quantity', e.target.value)} inputMode="decimal"  step="0.000001" className="w-full border border-[#E2E8F0] rounded px-2 py-1 text-right focus:ring-2 focus:ring-[#BDDDFC]" min="0" />
                          </td>
                          <td className="p-2 border" style={{ width: columnWidths.remark }}>
                            <input 
                              type="text"
                              value={line.Remark || ''}
                              onChange={e => handleLineChange(idx, 'Remark', e.target.value)}
                              className="w-full border border-[#E2E8F0] rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
                            />
                          </td>
                          <td className="p-2 border text-center" style={{ width: columnWidths.action }}>
                            <button type="button" className="text-red-500 hover:text-red-700 transition-colors p-1" onClick={() => removeLine(idx)} disabled={lines.length === 1}><TrashIcon /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </form>
          </div>

          {/* --- FOOTER --- */}
          <div className="flex-shrink-0 p-8 pt-0">
            <div className="flex justify-end gap-3">
              <button type="button" className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" onClick={onClose}>Cancel</button>
              <button type="submit" className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed flex items-center" disabled={loading || isSubmitting} onClick={handleSubmit}>
                {loading || isSubmitting ? 'Saving...' : (<><SaveIcon className="mr-2" size={18} />{initialData?.header?.MaterialReturnID ? 'Update Return' : 'Save Return'}</>)}
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* --- TOP CENTER TOAST --- */}
      {toast.open && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[99999] animate-toastIn">
          <div
            className={`
              relative w-[420px] rounded-2xl shadow-2xl border backdrop-blur-md overflow-hidden
              ${toast.type === 'error' && 'bg-red-50/90 border-red-200'}
              ${toast.type === 'warning' && 'bg-amber-50/90 border-amber-200'}
              ${toast.type === 'success' && 'bg-green-50/90 border-green-200'}
            `}
          >
            {/* Content */}
            <div className="flex gap-4 px-5 py-4">
              {/* Icon */}
              <div className="pt-1">
                {toast.type === 'error' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-100">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  </div>
                )}

                {toast.type === 'warning' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </div>
                )}
                
                {toast.type === 'success' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-green-100">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                )}
              </div>

              {/* Text */}
              <div className="flex-1">
                <div className="font-semibold text-sm text-gray-800 mb-1">
                  {toast.title}
                </div>
                <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                  {toast.message}
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div
              className={`
                absolute bottom-0 left-0 h-[3px] animate-toastProgress
                ${toast.type === 'error' && 'bg-red-500'}
                ${toast.type === 'warning' && 'bg-amber-500'}
                ${toast.type === 'success' && 'bg-green-500'}
              `}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default MaterialReturnFormModal;